<template>
  <div>
    <!-- First some general settings -->
    <!-- A textarea to enter the contact info -->
    <div class="flex flex-col items-center justify-center mb-6">
      <h1 class="text-2xl font-bold mb-4">Kontaktdaten eingeben</h1>
      <textarea class="textarea textarea-bordered w-full max-w-md"
                @keyup="updateAddress"
                @input="updateAddress"
                v-model="contactInfo"
                placeholder="Gib hier deine Adresse ein..."></textarea>
    </div>

    <!-- Then picking three designs from three dropdowns, all of them being rendered on the same canvas page -->
    <div class="flex flex-col items-center justify-center">
      <h1 class="text-2xl font-bold mb-4">Karten drucken</h1>
      <p class="mb-4">Wähle die Designs, die du drucken möchtest:</p>
      <div class="flex space-x-4 mb-6">
        <font-awesome-icon :icon="faSpinner" v-if="pending" class="animate-spin text-gray-500" />
        <p v-else-if="error" class="text-red-500">{{ error }}</p>
        <template v-for="d in selectedDesigns" :key="d.index" v-else>
          <div class="flex flex-col">
            <select v-model="d.value"
                  class="select select-bordered w-48 max-w-xs"
                    >
              <option value="" disabled selected>Design {{ d.index }}</option>
                    <option v-for="option in dataSorted" :key="option.id" :value="option.id">
                      {{ option.title }}
                    </option>
            </select>
            <select v-model="selectedTexts[d.index-1].value"
                  class="select select-bordered w-48 max-w-xs"
                    >
                    <option value="" selected>&mdash; Linienspiegel &mdash;</option>
                    <option value="no-lines" selected>&mdash; Weiße Seite &mdash;</option>
                    <option v-for="option in textSorted" :key="option.id" :value="option.id">
                      {{ option.title }}
                    </option>
            </select>
          </div>
        </template>
      </div>

      <!-- The canvas where the designs will be rendered -->
      <canvas ref="outsideRef" title="Vorschau Außenseite" class="border border-gray-300 mb-6 scale-50"
      style="width: 297mm; height: 210mm; background: #fff; margin-top: calc(-0.25 * 210mm); margin-bottom: calc(-0.25 * 210mm + 10px);">
      </canvas>
      <canvas ref="insideRef" title="Vorschau Innenseite" class="border border-gray-300 mb-6 scale-50"
              style="width: 297mm; height: 210mm; background: #fff; margin-top: calc(-0.25 * 210mm); margin-bottom: calc(-0.25 * 210mm + 10px);">
      </canvas>

      <!-- A button to generate the PDF -->
      <button class="btn btn-primary" @click="generatePDF">PDF Generieren (Drucken)</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useNuxtApp } from "nuxt/app";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {drawImage} from "~/composables/canvas-image";
import {drawCatchphrase} from "~/composables/canvas-catchphrase";
const { $surreal, $surrealQuery } = useNuxtApp()
import { jsPDF } from "jspdf";
import {RecordId} from "surrealdb";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

const outsideRef = useTemplateRef<InstanceType<typeof HTMLCanvasElement>>('outsideRef')
const insideRef = useTemplateRef<InstanceType<typeof HTMLCanvasElement>>('insideRef')

const { data, error, pending } = $surrealQuery('SELECT * FROM design')

const dataSorted = computed(() => {
  return data.value.sort((a, b) => a.title.localeCompare(b.title))
})

const { data: texts } = $surrealQuery('SELECT * FROM texts WHERE user = $auth.id')

const textSorted = computed(() => {
  return texts.value.sort((a, b) => a.title.localeCompare(b.title))
})

const { data: contactInfoLoaded, pending: contactInfoLoading } = $surrealQuery('SELECT id, lastAddress FROM $auth')
watch(contactInfoLoaded, (newValue) => {
  // Load from the database
  if (newValue && newValue.length > 0 && newValue[0]?.lastAddress) {
    contactInfo.value = newValue[0].lastAddress;
    updateAddress();
  }
});
watch(contactInfoLoading, () => {
  if (contactInfoLoaded.value && contactInfoLoaded.value.length > 0 && contactInfoLoaded.value[0]?.lastAddress) {
    contactInfo.value = contactInfoLoaded.value[0].lastAddress;
    updateAddress();
  }
})

const contactInfo = ref("");

const selectedDesigns = ref([
  { index: 1, value: ref("") },
  { index: 2, value: ref("") },
  { index: 3, value: ref("") },
])

const selectedTexts = ref([
  { index: 1, value: ref("") },
  { index: 2, value: ref("") },
  { index: 3, value: ref("") },
])

for (const d of selectedDesigns.value) {
  const idx = d.index;
  watch(d, (newValue) => {
    if (newValue) {
      updateDesign(newValue, idx);
    }
  });
}

for (const t of selectedTexts.value) {
  const idx = t.index;
  watch(t, (newValue) => {
    updateText(newValue, idx);
  });
}

// Important rendering info
const dpi = 300;
const mmWidth = 297 / 3;
const mmHeight = 210 / 2;
const toPixel = dpi / 25.4;

let outside: HTMLCanvasElement | null = null;
let ctxOutside: CanvasRenderingContext2D | null = null;

let inside: HTMLCanvasElement | null = null;
let ctxInside: CanvasRenderingContext2D | null = null;

function initCanvas() {
  if (!outside) {
    outside = outsideRef.value
    if (!outside) {
      console.warn('no outside')
      return
    }

    // This is "destructive"
    outside.width = mmWidth * toPixel * 3; // 3 designs per page
    outside.height = mmHeight * toPixel * 2; // front and back side
  }

  if (!ctxOutside) {
    ctxOutside = outside.getContext('2d')

    // Draw background
    ctxOutside.beginPath()
    ctxOutside.rect(0, 0, outside.width, outside.height);
    ctxOutside.fillStyle = '#fff';
    ctxOutside.fill();
  }
  if (!ctxOutside) {
    console.warn('no ctx outside')
    return
  }
  if (!inside) {
    inside = insideRef.value
    if (!inside) {
      console.warn('no inside')
      return
    }

    // This is "destructive"
    inside.width = mmWidth * toPixel * 3; // 3 designs per page
    inside.height = mmHeight * toPixel * 2; // front and back side
  }

  if (!ctxInside) {
    ctxInside = inside.getContext('2d')

    // Draw background
    ctxInside.beginPath()
    ctxInside.rect(0, 0, inside.width, inside.height);
    ctxInside.fillStyle = '#fff';
    ctxInside.fill();

    // TODO: Make this configurable and sync with SurrealDB
    // TODO: If "text" is selected, draw the text instead of the lines
    drawLines(ctxInside, 30, '#555');
  }
  if (!ctxInside) {
    console.warn('no ctx inside')
    return
  }
}

function drawLines(ctx: CanvasRenderingContext2D, amount: number, color: string, thickness = 1, onlyCol?: number) {
  if (!inside) {
    console.warn('no inside canvas for drawing lines');
    return;
  }

  // Clear page
  if (onlyCol === undefined) {
    ctx.clearRect(0, 0, inside.width, inside.height);
    ctx.beginPath()
    ctx.rect(0, 0, inside.width, inside.height);
    ctx.fillStyle = '#fff';
    ctx.fill();
  }

  // Draw lines
  ctx.beginPath();
  const distance = inside.height / amount;

  ctx.strokeStyle = color;
  ctx.lineWidth = thickness;

  for (let col = 0; col < 3; col++) {
    if (onlyCol !== undefined && col !== onlyCol) {
      continue; // Skip other columns if onlyCol is specified
    }

    let colStartX = col * inside.width / 3;

    for (let i = 0; i < amount; i++) {
      const isShorter = i === 0 || i === window.Math.round(amount / 2);

      // Draw horizontal lines, keeping the margin in mind
      let lineLength = inside.width / 3 - 2 * mmPrintMargin;
      let startX = colStartX + mmPrintMargin;

      if (isShorter) {
        const originalLineLength = lineLength;
        lineLength = lineLength * .65;
        startX = startX + (originalLineLength - lineLength) / 2;
      }

      ctx.setLineDash([3, 3]);
      ctx.moveTo(startX, (i + 1) * distance - 6 * toPixel);
      ctx.lineTo(startX + lineLength, (i + 1) * distance - 6 * toPixel);
    }
  }
  ctx.stroke();
}

async function updateAddress() {
  initCanvas();
  if (!ctxOutside || !outside) {
    console.warn('Canvas not ready for rendering');
    return;
  }

  // TODO: Update to SurrealDB (if needed)
  if (contactInfo.value && contactInfoLoaded?.value[0]?.lastAddress !== contactInfo.value) {
    // Save it locally, as if we already updated it (to prevent double updates)
    contactInfoLoaded.value = [{ id: contactInfoLoaded.value[0].id, lastAddress: contactInfo.value }];

    // Submit it to SurrealDB
    const userId = contactInfoLoaded?.value[0].id; // .toString()
    const surreal = await $surreal();
    // const res = await surreal.query(`UPDATE ${userId} SET lastAddress = $addr`, { addr: contactInfo.value });
    await surreal.merge(new RecordId(userId.tb, userId.id), { lastAddress: contactInfo.value });
  }

  // Clear half of the canvas
  ctxOutside.clearRect(0, 0, outside.width, outside.height / 2);
  ctxOutside.beginPath()
  ctxOutside.rect(0, 0, outside.width, outside.height / 2);
  ctxOutside.fillStyle = '#fff';
  ctxOutside.fill();

  drawAddress(contactInfo.value, 0);
  drawAddress(contactInfo.value, 1);
  drawAddress(contactInfo.value, 2);
}

function drawAddress(contactInfo: string, columnIndex: number) {
  if (!ctxOutside || !outside) {
    console.warn('Canvas not ready for rendering');
    return;
  }
  // Draw text
  ctxOutside.fillStyle = '#333';

  const lines = ("Meine Kontaktdaten\n\n" + contactInfo).split('\n');
  lines.splice(10); // Limit to 10 lines

  const lineHeight = 80;
  const lineCount = lines.length;
  const lineDistance = 2;

  const verticalCenterPadding = (outside.height / 2 - (lineCount * lineHeight + (lineCount - 1) * lineDistance)) / 2;

  for (let i = 0; i < lineCount; i++) {
    if (i === 0) {
      ctxOutside.font = 'bold 64pt Montserrat Semibold';
    } else {
      ctxOutside.font = '48pt Montserrat';
    }

    // Rotate the text we just wrote
    ctxOutside.save();
    ctxOutside.translate(outside.width / 3 * (columnIndex + 1), outside.height / 2); //  - (lineCount * lineHeight + (lineCount - 1) * lineDistance) / 2
    ctxOutside.rotate(-Math.PI);

    ctxOutside.fillText(lines[i], mmPrintMargin * 3, mmPrintMargin + (i * lineHeight) + (i * lineDistance) + verticalCenterPadding);
    ctxOutside.restore()
  }
}

async function updateText(selectedId: Ref<string>, index: number): Promise<void> {
  initCanvas();
  if (!ctxInside || !inside) {
    console.warn('Canvas not ready for rendering');
    return;
  }

  const selectedText = textSorted.value.find(t => t.id === selectedId.value);
  if (selectedText === undefined) {
    // No text found, so let's draw lines instead
    clearText(ctxInside, inside, inside.width/3, index-1);

    // If the selected ID is "no-lines", we don't draw lines
    if (selectedId.value !== "no-lines") {
     drawLines(ctxInside, 30, '#555', 1, index-1);
    }
    return;
  }

  clearText(ctxInside, inside, inside.width/3, index-1);
  drawText(ctxInside, inside, selectedText.contents, index-1);
}

async function updateDesign(selectedId: Ref<string>, index: number): Promise<void> {
  initCanvas();
  if (!ctxOutside || !outside) {
    console.warn('Canvas not ready for rendering');
    return;
  }

  const currentDesign = dataSorted.value.find(d => d.id === selectedId.value);
  if (currentDesign === undefined) {
    console.warn('No design found for index', index, selectedId.value);
    return;
  }

  const locX = outside.width / 3 * (index-1);
  const locY = (outside.height) / 2 // always on the lower half
  await drawImage(ctxOutside, currentDesign.image,  currentDesign.blurImage, locX, locY)
  await drawCatchphrase(outside, ctxOutside, currentDesign.catchPhrase, currentDesign.font, currentDesign.offsetX,  currentDesign.offsetY, locX)
}

function generatePDF() {
  if (!outside || !ctxOutside || !inside || !ctxInside) {
    console.warn('Canvas not ready for PDF generation');
    return;
  }

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: [mmHeight * 2, mmWidth * 3]
  });

  const imgOutside = outside.toDataURL('image/jpeg', 0.7);
  doc.addImage(imgOutside, 'JPEG', 0, 0, mmWidth * 3, mmHeight * 2, '', 'SLOW');

  doc.addPage();
  const imgInside = inside.toDataURL('image/jpeg', 0.7);
  doc.addImage(imgInside, 'JPEG', 0, 0, mmWidth * 3, mmHeight * 2, '', 'SLOW');

  doc.save('Karten.pdf');
}

</script>
